/**
 * Personal details collection page.
 *
 * Collects personal information from the applicant before the document
 * upload step. Includes required demographic fields, recommended contact
 * fields, and conditional EDD (Enhanced Due Diligence) fields that appear
 * when the `enhancedDueDiligence` flag is set on the project context.
 *
 * Fields are grouped into visual section cards: Identity, Contact, Address,
 * and (conditional) Enhanced Verification.
 *
 * @module pages/PersonalDetailsPage
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ApiClientError } from '@noxverify/api-client';
import type { applicant } from '@noxverify/api-contracts';
import type { ApplicantContext } from '../modules/context/context-loader';
import { getApiClient } from '../lib/api';
import { StepIndicator } from '../components/StepIndicator';
import { Footer } from '../components/Footer';

// ---------------------------------------------------------------------------
// Country list (ISO 3166-1 common subset)
// ---------------------------------------------------------------------------

const COUNTRIES = [
  { code: 'AF', label: 'Afghanistan' },
  { code: 'AL', label: 'Albania' },
  { code: 'DZ', label: 'Algeria' },
  { code: 'AR', label: 'Argentina' },
  { code: 'AU', label: 'Australia' },
  { code: 'AT', label: 'Austria' },
  { code: 'BD', label: 'Bangladesh' },
  { code: 'BE', label: 'Belgium' },
  { code: 'BR', label: 'Brazil' },
  { code: 'BG', label: 'Bulgaria' },
  { code: 'CA', label: 'Canada' },
  { code: 'CL', label: 'Chile' },
  { code: 'CN', label: 'China' },
  { code: 'CO', label: 'Colombia' },
  { code: 'HR', label: 'Croatia' },
  { code: 'CY', label: 'Cyprus' },
  { code: 'CZ', label: 'Czech Republic' },
  { code: 'DK', label: 'Denmark' },
  { code: 'EG', label: 'Egypt' },
  { code: 'EE', label: 'Estonia' },
  { code: 'FI', label: 'Finland' },
  { code: 'FR', label: 'France' },
  { code: 'DE', label: 'Germany' },
  { code: 'GR', label: 'Greece' },
  { code: 'HK', label: 'Hong Kong' },
  { code: 'HU', label: 'Hungary' },
  { code: 'IS', label: 'Iceland' },
  { code: 'IN', label: 'India' },
  { code: 'ID', label: 'Indonesia' },
  { code: 'IE', label: 'Ireland' },
  { code: 'IL', label: 'Israel' },
  { code: 'IT', label: 'Italy' },
  { code: 'JP', label: 'Japan' },
  { code: 'KE', label: 'Kenya' },
  { code: 'KR', label: 'South Korea' },
  { code: 'LV', label: 'Latvia' },
  { code: 'LT', label: 'Lithuania' },
  { code: 'LU', label: 'Luxembourg' },
  { code: 'MY', label: 'Malaysia' },
  { code: 'MT', label: 'Malta' },
  { code: 'MX', label: 'Mexico' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'NG', label: 'Nigeria' },
  { code: 'NO', label: 'Norway' },
  { code: 'PK', label: 'Pakistan' },
  { code: 'PE', label: 'Peru' },
  { code: 'PH', label: 'Philippines' },
  { code: 'PL', label: 'Poland' },
  { code: 'PT', label: 'Portugal' },
  { code: 'RO', label: 'Romania' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'SG', label: 'Singapore' },
  { code: 'SK', label: 'Slovakia' },
  { code: 'SI', label: 'Slovenia' },
  { code: 'ZA', label: 'South Africa' },
  { code: 'ES', label: 'Spain' },
  { code: 'SE', label: 'Sweden' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'TW', label: 'Taiwan' },
  { code: 'TH', label: 'Thailand' },
  { code: 'TR', label: 'Turkey' },
  { code: 'UA', label: 'Ukraine' },
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' },
  { code: 'VN', label: 'Vietnam' },
] as const;

const SOURCE_OF_FUNDS_KEYS = [
  'employment',
  'business',
  'investments',
  'inheritance',
  'savings',
  'other',
] as const;

function getSourceOfFundsOptions(t: TFunction): { value: string; label: string }[] {
  return SOURCE_OF_FUNDS_KEYS.map((key) => ({
    value: key,
    label: t(`personalDetails.sourceOfFundsOptions.${key}`),
  }));
}

// ---------------------------------------------------------------------------
// Section icons (inline SVGs)
// ---------------------------------------------------------------------------

function ShieldIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
      />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface PersonalDetailsForm {
  givenNames: string;
  familyName: string;
  dateOfBirth: string;
  nationality: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  addressCountry: string;
  // Recommended
  phoneNumber: string;
  countryOfResidence: string;
  // EDD conditional
  taxIdNumber: string;
  sourceOfFunds: string;
  sourceOfWealth: string;
  occupation: string;
  employerName: string;
}

const INITIAL_FORM: PersonalDetailsForm = {
  givenNames: '',
  familyName: '',
  dateOfBirth: '',
  nationality: '',
  email: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  addressCountry: '',
  phoneNumber: '',
  countryOfResidence: '',
  taxIdNumber: '',
  sourceOfFunds: '',
  sourceOfWealth: '',
  occupation: '',
  employerName: '',
};

type FieldErrors = Partial<Record<keyof PersonalDetailsForm, string>>;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function validateForm(
  form: PersonalDetailsForm,
  enhancedDueDiligence: boolean,
  t: TFunction,
): FieldErrors {
  const errors: FieldErrors = {};

  if (!form.givenNames.trim()) errors.givenNames = t('personalDetails.validation.givenNamesRequired');
  if (!form.familyName.trim()) errors.familyName = t('personalDetails.validation.familyNameRequired');
  if (!form.dateOfBirth) {
    errors.dateOfBirth = t('personalDetails.validation.dobRequired');
  } else {
    const dob = new Date(form.dateOfBirth);
    const now = new Date();
    if (dob >= now) errors.dateOfBirth = t('personalDetails.validation.dobPast');
  }
  if (!form.nationality) errors.nationality = t('personalDetails.validation.nationalityRequired');
  if (!form.email.trim()) {
    errors.email = t('personalDetails.validation.emailRequired');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = t('personalDetails.validation.emailInvalid');
  }
  if (!form.addressLine1.trim()) errors.addressLine1 = t('personalDetails.validation.addressLine1Required');
  if (!form.city.trim()) errors.city = t('personalDetails.validation.cityRequired');
  if (!form.postalCode.trim()) errors.postalCode = t('personalDetails.validation.postalCodeRequired');
  if (!form.addressCountry) errors.addressCountry = t('personalDetails.validation.countryRequired');

  if (enhancedDueDiligence) {
    if (!form.taxIdNumber.trim()) errors.taxIdNumber = t('personalDetails.validation.taxIdRequired');
    if (!form.sourceOfFunds) errors.sourceOfFunds = t('personalDetails.validation.sourceOfFundsRequired');
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Form → API contract mapping
// ---------------------------------------------------------------------------

/**
 * Map the internal form `sourceOfFunds` value to the API contract enum.
 * The form option `'business'` corresponds to the API value `'business_income'`.
 */
const SOURCE_OF_FUNDS_MAP: Record<string, applicant.SourceOfFunds> = {
  employment: 'employment',
  business: 'business_income',
  investments: 'investments',
  inheritance: 'inheritance',
  savings: 'savings',
  other: 'other',
};

/** Build a {@link applicant.PersonalDetailsInput} from the local form state. */
function toPersonalDetailsInput(
  form: PersonalDetailsForm,
): applicant.PersonalDetailsInput {
  const input: applicant.PersonalDetailsInput = {
    givenNames: form.givenNames.trim(),
    familyName: form.familyName.trim(),
    dateOfBirth: form.dateOfBirth,
    nationality: form.nationality,
    email: form.email.trim(),
    address: {
      line1: form.addressLine1.trim(),
      line2: form.addressLine2.trim() || undefined,
      city: form.city.trim(),
      stateOrProvince: form.stateProvince.trim() || undefined,
      postalCode: form.postalCode.trim(),
      country: form.addressCountry,
    },
  };

  // Recommended fields — only include when provided.
  if (form.phoneNumber.trim()) input.phoneNumber = form.phoneNumber.trim();
  if (form.countryOfResidence) input.countryOfResidence = form.countryOfResidence;

  // EDD conditional fields — only include when provided.
  if (form.taxIdNumber.trim()) input.taxIdNumber = form.taxIdNumber.trim();
  if (form.sourceOfFunds) {
    input.sourceOfFunds = SOURCE_OF_FUNDS_MAP[form.sourceOfFunds] ?? undefined;
  }
  if (form.sourceOfWealth.trim()) input.sourceOfWealth = form.sourceOfWealth.trim();
  if (form.occupation.trim()) input.occupation = form.occupation.trim();
  if (form.employerName.trim()) input.employerName = form.employerName.trim();

  return input;
}

// ---------------------------------------------------------------------------
// Reusable form field components
// ---------------------------------------------------------------------------

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  type?: 'text' | 'email' | 'tel' | 'date';
  placeholder?: string;
  autoComplete?: string;
  helperText?: string;
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  required = false,
  type = 'text',
  placeholder,
  autoComplete,
  helperText,
}: TextFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-muted">
        {label}{required && ' *'}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={
          [error ? `${id}-error` : null, helperText ? `${id}-helper` : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        className={[
          'block h-11 w-full rounded-lg border px-3 py-2 text-sm text-ink transition-all duration-200',
          'placeholder:text-ink-muted',
          'focus:outline-none focus:ring-1',
          error
            ? 'border-danger/40 focus:border-danger focus:ring-danger/30'
            : 'border-hairline-strong focus:border-[var(--nv-primary-color)] focus:ring-[var(--nv-primary-color)]',
        ].join(' ')}
      />
      {helperText && !error && (
        <p id={`${id}-helper`} className="mt-1 text-xs text-ink-muted">
          {helperText}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { readonly value?: string; readonly code?: string; readonly label: string }[];
  error?: string;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
}

function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  error,
  required = false,
  placeholder,
  helperText,
}: SelectFieldProps) {
  const { t } = useTranslation('portal');
  const effectivePlaceholder = placeholder ?? t('personalDetails.fields.selectPlaceholder');
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-muted">
        {label}{required && ' *'}
      </label>
      <select
        id={id}
        name={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={
          [error ? `${id}-error` : null, helperText ? `${id}-helper` : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        className={[
          'block h-11 w-full rounded-lg border px-3 py-2 text-sm text-ink transition-all duration-200',
          'focus:outline-none focus:ring-1',
          !value ? 'text-ink-muted' : '',
          error
            ? 'border-danger/40 focus:border-danger focus:ring-danger/30'
            : 'border-hairline-strong focus:border-[var(--nv-primary-color)] focus:ring-[var(--nv-primary-color)]',
        ].join(' ')}
      >
        <option value="" disabled>
          {effectivePlaceholder}
        </option>
        {options.map((opt) => {
          const optValue = opt.value ?? opt.code ?? '';
          return (
            <option key={optValue} value={optValue}>
              {opt.label}
            </option>
          );
        })}
      </select>
      {helperText && !error && (
        <p id={`${id}-helper`} className="mt-1 text-xs text-ink-muted">
          {helperText}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface TextAreaFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  rows?: number;
}

function TextAreaField({
  id,
  label,
  value,
  onChange,
  error,
  required = false,
  placeholder,
  rows = 3,
}: TextAreaFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-muted">
        {label}{required && ' *'}
      </label>
      <textarea
        id={id}
        name={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        rows={rows}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={[
          'block w-full rounded-lg border px-3 py-2 text-sm text-ink transition-all duration-200',
          'placeholder:text-ink-muted',
          'focus:outline-none focus:ring-1',
          error
            ? 'border-danger/40 focus:border-danger focus:ring-danger/30'
            : 'border-hairline-strong focus:border-[var(--nv-primary-color)] focus:ring-[var(--nv-primary-color)]',
        ].join(' ')}
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section card wrapper
// ---------------------------------------------------------------------------

interface SectionCardProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

function SectionCard({ icon, title, children }: SectionCardProps) {
  return (
    <section className="rounded-lg border border-hairline bg-white p-6">
      <div className="mb-4 flex items-center gap-2.5">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-lg text-white"
          style={{ backgroundColor: 'var(--nv-primary-color)' }}
          aria-hidden="true"
        >
          {icon}
        </span>
        <h3 className="text-lg font-semibold text-ink">{title}</h3>
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

interface PersonalDetailsPageProps {
  context: ApplicantContext;
  /** When true, show Enhanced Due Diligence fields. */
  enhancedDueDiligence?: boolean;
}

export function PersonalDetailsPage({
  context,
  enhancedDueDiligence = false,
}: PersonalDetailsPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { caseId } = useParams<{ caseId: string }>();
  const [searchParams] = useSearchParams();
  const submissionSetId = searchParams.get('ss');
  const sourceOfFundsOptions = useMemo(() => getSourceOfFundsOptions(t), [t]);

  const [form, setForm] = useState<PersonalDetailsForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const updateField = useCallback(
    <K extends keyof PersonalDetailsForm>(field: K, value: PersonalDetailsForm[K]) => {
      setForm((prev) => {
        const next = { ...prev, [field]: value };
        // Auto-fill country of residence from nationality when not yet set
        if (field === 'nationality' && !prev.countryOfResidence) {
          next.countryOfResidence = value;
        }
        return next;
      });
      // Clear field-level error on change
      if (errors[field]) {
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy[field];
          return copy;
        });
      }
    },
    [errors],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitted(true);
      setApiError(null);

      const validationErrors = validateForm(form, enhancedDueDiligence, t);
      setErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        // Focus the first errored field
        const firstErrorField = Object.keys(validationErrors)[0] as keyof PersonalDetailsForm;
        const el = document.getElementById(firstErrorField);
        el?.focus();
        return;
      }

      if (!caseId) return;

      // NOTE: Previously the entire PII form (names, DOB, nationality, email,
      // address, tax ID, source-of-funds, source-of-wealth, occupation,
      // employer) was written to sessionStorage as a "local cache / backup".
      // sessionStorage is plaintext and accessible to any script running in
      // the document — that is a PII exposure with no compensating control.
      // The API call below persists the data server-side; a draft-recovery
      // UX, if needed, is a separate feature with its own threat model.

      // Submit personal details to the API.
      setSubmitting(true);

      try {
        const client = getApiClient();
        const payload = toPersonalDetailsInput(form);
        await client.applicant.savePersonalDetails(caseId, payload);

        // Navigate only after the API call succeeds.
        const ssParam = submissionSetId ? `?ss=${submissionSetId}` : '';
        navigate(`/case/${caseId}/documents${ssParam}`);
      } catch (err: unknown) {
        const apiErr = err as ApiClientError;
        setApiError(
          apiErr.message ??
            t('personalDetails.saveFailed'),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [form, enhancedDueDiligence, caseId, submissionSetId, navigate, t],
  );

  // Missing case ID.
  if (!caseId) {
    return (
      <div className="flex min-h-screen flex-col bg-surface">
        <main className="flex flex-1 items-center justify-center">
          <div className="rounded-lg border border-hairline bg-surface-raised p-8 text-center" role="alert">
            <h2 className="mb-2 text-xl font-bold text-ink">{t('app.missingCase')}</h2>
            <p className="mb-4 text-sm text-ink-muted">
              {t('app.missingCaseDescription')}
            </p>
            <a
              href="/"
              className="inline-block rounded-lg px-6 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: 'var(--nv-primary-color)' }}
            >
              {t('app.goToStart')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Header */}
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-2xl items-center gap-4 px-4 py-4">
          {context.branding.logoUrl && (
            <img
              src={context.branding.logoUrl}
              alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
              className="h-8"
            />
          )}
          <h1
            className="text-lg font-semibold"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {t('app.identityVerification')}
          </h1>
        </div>
      </header>

      {/* Step indicator */}
      <div className="mx-auto w-full max-w-2xl px-4">
        <StepIndicator currentStep="details" />
      </div>

      <main className="flex-1">
        <div className="mx-auto max-w-2xl px-4 py-8">
          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mb-6 inline-flex h-11 items-center gap-1.5 rounded-lg border border-hairline bg-white px-4 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken hover:text-ink focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('app.back')}
          </button>

          {/* Page title */}
          <div className="mb-6 text-center">
            <div className="mb-2 flex items-center justify-center gap-2">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
                style={{ color: 'var(--nv-primary-color)' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                />
              </svg>
              <h2 className="font-display text-3xl font-medium tracking-tight text-ink">
                {t('personalDetails.title')}
              </h2>
            </div>
            <p className="text-sm text-ink-muted">
              {t('personalDetails.description')}
            </p>
          </div>

          {/* Validation summary when submitted with errors */}
          {submitted && Object.keys(errors).length > 0 && (
            <div
              role="alert"
              className="mb-6 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
            >
              {t('personalDetails.validationSummary')}
            </div>
          )}

          {/* API error banner */}
          {apiError && (
            <div
              role="alert"
              className="mb-6 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
            >
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* ---- Identity section ---- */}
            <SectionCard icon={<ShieldIcon />} title={t('personalDetails.sections.identity')}>
              <fieldset>
                <legend className="sr-only">{t('personalDetails.sections.identityLegend')}</legend>
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    id="givenNames"
                    label={t('personalDetails.fields.givenNames.label')}
                    value={form.givenNames}
                    onChange={(v) => updateField('givenNames', v)}
                    error={errors.givenNames}
                    required
                    placeholder={t('personalDetails.fields.givenNames.placeholder')}
                    autoComplete="given-name"
                  />
                  <TextField
                    id="familyName"
                    label={t('personalDetails.fields.familyName.label')}
                    value={form.familyName}
                    onChange={(v) => updateField('familyName', v)}
                    error={errors.familyName}
                    required
                    placeholder={t('personalDetails.fields.familyName.placeholder')}
                    autoComplete="family-name"
                  />
                  <TextField
                    id="dateOfBirth"
                    label={t('personalDetails.fields.dateOfBirth.label')}
                    value={form.dateOfBirth}
                    onChange={(v) => updateField('dateOfBirth', v)}
                    error={errors.dateOfBirth}
                    required
                    type="date"
                    autoComplete="bday"
                    helperText={t('personalDetails.fields.dateOfBirth.helper')}
                  />
                  <SelectField
                    id="nationality"
                    label={t('personalDetails.fields.nationality.label')}
                    value={form.nationality}
                    onChange={(v) => updateField('nationality', v)}
                    options={COUNTRIES}
                    error={errors.nationality}
                    required
                    placeholder={t('personalDetails.fields.nationality.placeholder')}
                    helperText={t('personalDetails.fields.nationality.helper')}
                  />
                </div>
              </fieldset>
            </SectionCard>

            {/* ---- Contact section ---- */}
            <SectionCard icon={<PhoneIcon />} title={t('personalDetails.sections.contact')}>
              <fieldset>
                <legend className="sr-only">{t('personalDetails.sections.contactLegend')}</legend>
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    id="email"
                    label={t('personalDetails.fields.email.label')}
                    value={form.email}
                    onChange={(v) => updateField('email', v)}
                    error={errors.email}
                    required
                    type="email"
                    placeholder={t('personalDetails.fields.email.placeholder')}
                    autoComplete="email"
                  />
                  <TextField
                    id="phoneNumber"
                    label={t('personalDetails.fields.phone.label')}
                    value={form.phoneNumber}
                    onChange={(v) => updateField('phoneNumber', v)}
                    error={errors.phoneNumber}
                    type="tel"
                    placeholder={t('personalDetails.fields.phone.placeholder')}
                    autoComplete="tel"
                  />
                </div>
              </fieldset>
            </SectionCard>

            {/* ---- Address section ---- */}
            <SectionCard icon={<MapPinIcon />} title={t('personalDetails.sections.address')}>
              <fieldset>
                <legend className="sr-only">{t('personalDetails.sections.addressLegend')}</legend>
                <div className="space-y-4">
                  <TextField
                    id="addressLine1"
                    label={t('personalDetails.fields.addressLine1.label')}
                    value={form.addressLine1}
                    onChange={(v) => updateField('addressLine1', v)}
                    error={errors.addressLine1}
                    required
                    placeholder={t('personalDetails.fields.addressLine1.placeholder')}
                    autoComplete="address-line1"
                  />
                  <TextField
                    id="addressLine2"
                    label={t('personalDetails.fields.addressLine2.label')}
                    value={form.addressLine2}
                    onChange={(v) => updateField('addressLine2', v)}
                    placeholder={t('personalDetails.fields.addressLine2.placeholder')}
                    autoComplete="address-line2"
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                      id="city"
                      label={t('personalDetails.fields.city.label')}
                      value={form.city}
                      onChange={(v) => updateField('city', v)}
                      error={errors.city}
                      required
                      placeholder={t('personalDetails.fields.city.placeholder')}
                      autoComplete="address-level2"
                    />
                    <TextField
                      id="stateProvince"
                      label={t('personalDetails.fields.stateProvince.label')}
                      value={form.stateProvince}
                      onChange={(v) => updateField('stateProvince', v)}
                      error={errors.stateProvince}
                      placeholder={t('personalDetails.fields.stateProvince.placeholder')}
                      autoComplete="address-level1"
                    />
                    <TextField
                      id="postalCode"
                      label={t('personalDetails.fields.postalCode.label')}
                      value={form.postalCode}
                      onChange={(v) => updateField('postalCode', v)}
                      error={errors.postalCode}
                      required
                      placeholder={t('personalDetails.fields.postalCode.placeholder')}
                      autoComplete="postal-code"
                    />
                    <SelectField
                      id="addressCountry"
                      label={t('personalDetails.fields.country.label')}
                      value={form.addressCountry}
                      onChange={(v) => updateField('addressCountry', v)}
                      options={COUNTRIES}
                      error={errors.addressCountry}
                      required
                      placeholder={t('personalDetails.fields.country.placeholder')}
                    />
                  </div>
                  <SelectField
                    id="countryOfResidence"
                    label={t('personalDetails.fields.countryOfResidence.label')}
                    value={form.countryOfResidence}
                    onChange={(v) => updateField('countryOfResidence', v)}
                    options={COUNTRIES}
                    placeholder={t('personalDetails.fields.countryOfResidence.placeholder')}
                  />
                </div>
              </fieldset>
            </SectionCard>

            {/* ---- EDD fields (conditional) ---- */}
            {enhancedDueDiligence && (
              <SectionCard icon={<LockIcon />} title={t('personalDetails.sections.enhanced')}>
                <fieldset>
                  <legend className="sr-only">{t('personalDetails.sections.enhancedLegend')}</legend>
                  <div
                    className="mb-4 rounded-md border border-info/30 bg-info-tint px-4 py-3 text-sm text-info"
                    role="note"
                  >
                    {t('personalDetails.eddNote')}
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                      id="taxIdNumber"
                      label={t('personalDetails.fields.taxId.label')}
                      value={form.taxIdNumber}
                      onChange={(v) => updateField('taxIdNumber', v)}
                      error={errors.taxIdNumber}
                      required
                      placeholder={t('personalDetails.fields.taxId.placeholder')}
                    />
                    <SelectField
                      id="sourceOfFunds"
                      label={t('personalDetails.fields.sourceOfFunds.label')}
                      value={form.sourceOfFunds}
                      onChange={(v) => updateField('sourceOfFunds', v)}
                      options={sourceOfFundsOptions}
                      error={errors.sourceOfFunds}
                      required
                      placeholder={t('personalDetails.fields.sourceOfFunds.placeholder')}
                    />
                    <div className="sm:col-span-2">
                      <TextAreaField
                        id="sourceOfWealth"
                        label={t('personalDetails.fields.sourceOfWealth.label')}
                        value={form.sourceOfWealth}
                        onChange={(v) => updateField('sourceOfWealth', v)}
                        error={errors.sourceOfWealth}
                        placeholder={t('personalDetails.fields.sourceOfWealth.placeholder')}
                        rows={3}
                      />
                    </div>
                    <TextField
                      id="occupation"
                      label={t('personalDetails.fields.occupation.label')}
                      value={form.occupation}
                      onChange={(v) => updateField('occupation', v)}
                      error={errors.occupation}
                      placeholder={t('personalDetails.fields.occupation.placeholder')}
                      autoComplete="organization-title"
                    />
                    <TextField
                      id="employerName"
                      label={t('personalDetails.fields.employerName.label')}
                      value={form.employerName}
                      onChange={(v) => updateField('employerName', v)}
                      error={errors.employerName}
                      placeholder={t('personalDetails.fields.employerName.placeholder')}
                      autoComplete="organization"
                    />
                  </div>
                </fieldset>
              </SectionCard>
            )}

            {/* ---- Progress + Submit ---- */}
            <div className="rounded-lg border border-hairline bg-white p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-ink-muted">
                  {t('personalDetails.step')}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md px-8 font-semibold text-white transition-all duration-200 hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    backgroundColor: 'var(--nv-primary-color)',
                    '--tw-ring-color': 'var(--nv-primary-color)',
                  } as React.CSSProperties}
                >
                  {submitting && (
                    <span
                      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                      aria-hidden="true"
                    />
                  )}
                  {submitting ? t('personalDetails.saving') : t('personalDetails.continue')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>

      <Footer branding={context.branding} />
    </div>
  );
}
